import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ICONS from "../../../../../assets/Icons";
import { beneCreateAccount } from "../../../../../redux/features/accounts/BeneficiaryAccount/createAccount";
import globalText from "../../../../../utils/globalText";
import AdditionalRecipients from "../../../../_utils/AccountCreationComponents/AdditionalRecipients";
import AssignLabels from "../../../../_utils/AccountCreationComponents/AssignLabels";
import CategoriesEdit from "../../../../_utils/AccountCreationComponents/CategoriesEdit";
import ContactDetails from "../../../../_utils/AccountCreationComponents/ContactDetails";
import GstDetails from "../../../../_utils/AccountCreationComponents/GstDetails";
import PanDetails from "../../../../_utils/AccountCreationComponents/PanDetails";
import CommonBankLogo from "../../../../_utils/CommonBankLogo";
import CommonLayout from "../../../../_utils/CommonLayout";
import SmallPopup from "../../../../_utils/CommonSmallPopup";
import CommonToast from "../../../../_utils/CommonToast";
import { formatAccountNumber } from "../../../../_utils/FormatAccountNumber";
import ConfidentialCard from "../../../../_utils/TransactionCommonComponents/ConfidentialCard";
import BeneAccountViewAllPopup from "../../BeneAccountViewAllPopup";
import "./index.css";

const BeneAccountStepConfirm = ({
  payload,
  selectedFields,
  bankAccountName,
  setShowModalPopup,
  showModalPopup,
  bankUserInfo,
  recipients,
  flow,
  remPan,
  setRemPan,
  isSubmitting,
  setIsSubmitting,
  manualVirtualAccount,
  handleCreateAccount,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const categories = useSelector(
    (state) => state.beneAllVerification.beneTransCategories.categories,
  );
  const accountType = location.pathname.includes("beneficiary_account")
    ? "beneficiary"
    : location.pathname.includes("internal_account")
      ? "internal"
      : "";

  const [remIFSC, setRemIFSC] = useState("");
  const [accountId, setAccountId] = useState(null);
  const [remAccount, setRemAccount] = useState("");
  const [activeType, setActiveType] = useState("pan");
  const [isalertPopup, setAlertPopUp] = useState(false);
  // pendingNavTarget holds the account detail path a card tried to navigate to
  const [pendingNavTarget, setPendingNavTarget] = useState(null);
  const [openBeneAssociate, setOpenBeneAssociate] = useState(false);

  const handleAccountSelection = (beneAccountId) => {
    setAccountId(beneAccountId);
  };

  const handleCloseModal = () => {
    if (isalertPopup) return;
    setShowModalPopup(false);
  };

  const handleCardClick = (targetPath) => {
    setPendingNavTarget(targetPath);
    setAlertPopUp(true);
  };

  const handleLeaveConfirm = () => {
    setAlertPopUp(false);
    setShowModalPopup(false);
    if (pendingNavTarget) {
      navigate(pendingNavTarget);
    } else {
      navigate("/beneficiary_account_dashboard/beneficiary_account_view_all");
    }
    setPendingNavTarget(null);
  };

  const handleAlertCancel = () => {
    setAlertPopUp(false);
    setPendingNavTarget(null);
  };

  return (
    <>
      {isalertPopup && (
        <div
          className="fixed z-z-index-max flex items-center justify-center top-left-base left-left-base w-width-full h-height-full bg-PopUp-BackDrop bg-opacity-dark-opacity"
          onClick={(e) => e.stopPropagation()}
        >
          <SmallPopup
            headerTitle="Open in full view?"
            headerFontWeight="semibold"
            headerFontColor="emphasis"
            textSize="font-lg"
            subtext="Leaving this page will exit you from current flow."
            btnOneText="Cancel"
            btnOneHandleFunction={handleAlertCancel}
            btnOneEnabledBgColor="var(--primary-bg)"
            btnOneOutLineColor="var(--text-primary)"
            btnOneEnabledTextColor="var(--text-primary)"
            btnOneIsDisabled={false}
            btnOneHoverOption="white"
            btnTwoText="Leave"
            btnTwoHandleFunction={handleLeaveConfirm}
            btnTwoEnabledBgColor="var(--text-primary)"
            btnTwoEnabledTextColor="var(--primary-bg)"
            btnTwoIsDisabled={false}
            btnTwoDisabledBgColor="var(--text-disabled)"
            btnTwoIsLoaderNeeded={true}
            btnTwoHoverOption="black"
          />
        </div>
      )}

      {showModalPopup && (
        <BeneAccountViewAllPopup
          onClose={handleCloseModal}
          onAccountSelect={handleAccountSelection}
          setOpenBeneAssociate={setOpenBeneAssociate}
          associateAccount={true}
          associatepanNumber={payload.panNumber}
          associateIFSC={payload.ifsc_code}
          associateaccountNumber={payload.bank_account}
          beneAccountId={payload.id}
          accountType={accountType}
          flow={"beneTxn"}
          ispanbankRequired={true}
          type={"viewOnlyCards"}
          subType={flow === "reimbursement" ? flow : ""}
          handleClick={handleCreateAccount}
          showModalPopup={showModalPopup}
          setAlertPopUp={setAlertPopUp}
          flowType={"create"}
          activeType={activeType}
          setActiveType={setActiveType}
          setRemPan={setRemPan}
          setRemAccount={setRemAccount}
          setRemIFSC={setRemIFSC}
          remIFSC={remIFSC}
          remAccount={remAccount}
          remPan={remPan}
          typePanBank={"BeneAccPanBankSwitch"}
          createDisabled={isSubmitting}
          onCardNavigate={handleCardClick}
        />
      )}

      <div className="px-padding-md lg:px-padding-base py-padding-md Qsmd:py-padding-base max-w-tab-width-normal w-full mx-auto Qsmd:!pb-padding-md">
        <CommonLayout>
          <PanDetails
            previewMode={true}
            panNumber={payload.panNumber}
            legalName={payload.legalName}
          />

          <GstDetails
            previewMode={true}
            gstin={payload?.gstin}
            gst_org_name={payload?.gst_org_name}
          />

          <div className="flex flex-col bg-primary-card rounded-border-radius-md gap-gap-md p-padding-md">
            <div className="text-text-primary font-semibold text-font-md">
              Bank Details
            </div>
            <div className="flex flex-col gap-gap-sm">
              <div className="flex flex-col gap-gap-xs">
                <div className="w-width-full text-text-primary font-normal capitalize text-font-md truncate">
                  {formatAccountNumber(payload?.bank_account)}
                </div>
                <div className="text-font-xs font-normal text-text-secondary">
                  Bank account number
                </div>
              </div>
              <div className="flex flex-row justify-between items-center">
                <div className="flex flex-col gap-gap-xs">
                  <div className="text-text-primary font-normal capitalize text-font-md truncate">
                    {payload?.ifsc_code}
                  </div>
                  <div className="text-font-xs font-normal text-text-secondary">
                    IFSC code
                  </div>
                </div>
                <CommonBankLogo
                  ifsc={payload?.ifsc_code}
                  variant="big"
                  type="pill"
                />
              </div>
              <>
                <div className="bg-primary-bg h-[var(--border)] opacity-base-opacity my-margin-xs"></div>
                <div className="flex flex-col gap-gap-xs">
                  <div className="w-width-full text-text-primary font-normal capitalize text-font-md truncate">
                    {payload?.legalName?.toLowerCase()}
                  </div>
                  <div className="text-font-xs font-normal text-text-secondary">
                    Account holder's name
                  </div>
                </div>
                {bankUserInfo.length >= 1 ? (
                  <>
                    <div className="flex flex-col gap-gap-xs">
                      <div className="w-width-full text-text-primary font-normal capitalize text-font-md truncate">
                        {bankUserInfo[0].branch ? bankUserInfo[0].branch : "NA"}
                      </div>
                      <div className="text-font-xs font-normal text-text-secondary">
                        Branch
                      </div>
                    </div>
                    <div className="flex flex-col gap-gap-xs">
                      <div className="w-width-full text-text-primary font-normal capitalize text-font-md truncate">
                        {bankUserInfo[0].city ? bankUserInfo[0].city : "NA"}
                      </div>
                      <div className="text-font-xs font-normal text-text-secondary">
                        City
                      </div>
                    </div>
                    <div className="flex flex-col gap-gap-xs">
                      <div className="w-width-full text-text-primary font-normal capitalize text-font-md truncate">
                        {bankUserInfo[0].district
                          ? bankUserInfo[0].district
                          : "NA"}
                      </div>
                      <div className="text-font-xs font-normal text-text-secondary">
                        District
                      </div>
                    </div>
                    <div className="flex flex-col gap-gap-xs">
                      <div
                        className="w-width-full text-text-primary font-normal capitalize text-font-md"
                        style={{ textWrap: "wrap", wordWrap: "break-word" }}
                      >
                        {bankUserInfo[0].address
                          ? bankUserInfo[0].address
                          : "NA"}
                      </div>
                      <div className="text-font-xs font-normal text-text-secondary">
                        Address
                      </div>
                    </div>
                  </>
                ) : (
                  <></>
                )}
              </>
            </div>
          </div>
          <div className="flex flex-col gap-gap-md">
            <ContactDetails
              previewMode={true}
              accountName={payload?.account_name}
              contactNumber={payload?.contact_no}
              emailId={payload?.email_id}
            />

            {manualVirtualAccount && (
              <div>
                <ConfidentialCard
                  icon={ICONS.VIRTUAL_ACCOUNT}
                  title={globalText?.MARK_VIRTUAL_ACCOUNT}
                  note={globalText?.VIRTUAL_ACCOUNT_NOTE}
                  toggleState={manualVirtualAccount}
                  isPreview={true}
                />
              </div>
            )}
          </div>

          <CategoriesEdit
            sortedCategories={categories}
            selectedCategories={selectedFields}
            Title="Beneficiary Categories"
            isPreview={true}
          />

          <div className="Qlg:pb-padding-md pb-padding-base">
            <AssignLabels
              previewMode={true}
              labels={payload?.labels}
              isOptional={true}
            />
          </div>

          <div className=" pb-padding-md">
            <AdditionalRecipients
              previewMode={true}
              recipients={recipients}
            />
          </div>
        </CommonLayout>
      </div>
    </>
  );
};

export default BeneAccountStepConfirm;
